import { useEffect, useRef } from 'react';
import { API } from '@/App';

/**
 * Anonymous presence heartbeat — pings backend every 30s with a stable per-tab
 * session id (kept in sessionStorage so it survives soft refreshes but resets
 * per tab/browser). Stops on page hide / unload.
 *
 * The session id is opaque random (crypto.randomUUID) — no PII.
 */
function getOrCreateSessionId() {
  try {
    let sid = sessionStorage.getItem('presence_sid');
    if (!sid) {
      sid = (crypto.randomUUID && crypto.randomUUID()) ||
            `sid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem('presence_sid', sid);
    }
    return sid;
  } catch {
    return `sid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

export function usePresenceHeartbeat(intervalMs = 30000) {
  const timerRef = useRef(null);

  useEffect(() => {
    const sid = getOrCreateSessionId();
    const ping = async () => {
      if (document.hidden) return;  // skip when tab is in background
      try {
        await fetch(`${API}/presence/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: sid,
            path: window.location.pathname,
          }),
          keepalive: true,
        });
      } catch {
        // network blocked / offline — non-fatal
      }
    };

    ping();  // immediate first ping
    timerRef.current = setInterval(ping, intervalMs);

    const onVisible = () => { if (!document.hidden) ping(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [intervalMs]);
}
